import { useState, useCallback, useRef } from 'react';

const INITIAL_BOARD = [
  [0, 3, 2, 0, 0],
  [0, 3, 0, 1, 0],
  [2, 1, 1, 2, 2],
  [0, 1, 2, 3, 0],
  [0, 3, 2, 0, 0],
];

const CELL_BG   = ['var(--cell-void)', 'var(--cell-blue)', 'var(--cell-violet)', 'var(--cell-green)', 'var(--cell-orange)', 'var(--cell-pink)'];
const NEON      = ['transparent',      'var(--neon-blue)', 'var(--neon-violet)', 'var(--neon-green)', 'var(--neon-orange)', 'var(--neon-pink)'];

function getRipplePath(board, row, col) {
  const val = board[row][col];
  if (val === 0) return new Set();
  const path = new Set();
  const dirs = [[-1,0],[1,0],[0,-1],[0,1]];
  for (const [dr, dc] of dirs) {
    for (let s = 1; s <= val; s++) {
      const r = row + dr * s;
      const c = col + dc * s;
      if (r >= 0 && r < 5 && c >= 0 && c < 5) path.add(`${r},${c}`);
    }
  }
  return path;
}

function applyRipple(board, row, col) {
  const val = board[row][col];
  if (val === 0) return board;
  const next = board.map(r => [...r]);
  next[row][col] = Math.max(0, next[row][col] - 1);
  const dirs = [[-1,0],[1,0],[0,-1],[0,1]];
  for (const [dr, dc] of dirs) {
    for (let s = 1; s <= val; s++) {
      const r = row + dr * s;
      const c = col + dc * s;
      if (r >= 0 && r < 5 && c >= 0 && c < 5 && next[r][c] > 0) {
        next[r][c]--;
      }
    }
  }
  return next;
}

function isSolved(board) {
  return board.every(row => row.every(v => v === 0));
}

export default function GridDemo() {
  const [board, setBoard]       = useState(INITIAL_BOARD.map(r => [...r]));
  const [preview, setPreview]   = useState(new Set());
  const [flashing, setFlashing] = useState(new Set());
  const flashTimerRef           = useRef(null);

  const handleHover = useCallback((row, col) => {
    setPreview(getRipplePath(board, row, col));
  }, [board]);

  const handleLeave = useCallback(() => setPreview(new Set()), []);

  const handleClick = useCallback((row, col) => {
    if (board[row][col] === 0) return;
    const path = getRipplePath(board, row, col);
    const newBoard = applyRipple(board, row, col);

    const toFlash = new Set([`${row},${col}`, ...path]);
    setFlashing(toFlash);
    setPreview(new Set());

    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setFlashing(new Set());
      setBoard(newBoard);
    }, 300);
  }, [board]);

  const handleReset = useCallback(() => {
    setBoard(INITIAL_BOARD.map(r => [...r]));
    setPreview(new Set());
    setFlashing(new Set());
  }, []);

  const solved = isSolved(board);

  return (
    <div className="grid-demo">
      <div className="grid-demo-label">Try it — tap a cell</div>
      <div className="grid-board">
        {board.map((row, ri) =>
          row.map((val, ci) => {
            const key = `${ri},${ci}`;
            const isPreview = preview.has(key);
            const isFlash   = flashing.has(key);
            const neon = NEON[val] || 'transparent';
            return (
              <div
                key={key}
                className={`grid-cell ${isPreview ? 'ripple-preview' : ''} ${isFlash ? 'ripple-flash' : ''}`}
                style={{
                  background: CELL_BG[val] || CELL_BG[0],
                  color: neon,
                  cursor: val > 0 ? 'pointer' : 'default',
                  '--glow': neon,
                }}
                onMouseEnter={() => handleHover(ri, ci)}
                onMouseLeave={handleLeave}
                onTouchStart={() => handleHover(ri, ci)}
                onTouchEnd={handleLeave}
                onTouchCancel={handleLeave}
                onClick={() => handleClick(ri, ci)}
                role="button"
                aria-label={val > 0 ? `Cell value ${val}` : 'Empty cell'}
                tabIndex={val > 0 ? 0 : -1}
                onKeyDown={e => e.key === 'Enter' && handleClick(ri, ci)}
              >
                {val > 0 ? val : null}
              </div>
            );
          })
        )}
      </div>
      <div className="grid-demo-controls">
        {solved
          ? <div className="grid-solved-msg">Board cleared! ✓</div>
          : <button className="btn-ghost" onClick={handleReset}>Reset</button>
        }
        {solved && (
          <button className="btn-ghost" onClick={handleReset}>Play again</button>
        )}
      </div>
    </div>
  );
}
